import { ApPayee } from '@/components/ApPayee';
import { KeywordCont } from '@/components/KeywordCont';
import { OfficeMember } from '@/components/OfficeMember';
import { useGetPopupContainer } from '@/components/ReportForm/Container/PopupContainer';
import { Amount } from '@/features/invoice_reports/components/Search/components/Amount';
import { InvoiceMFFileStatusesSelect } from '@/features/invoice_reports/components/Search/components/InvoiceMFFileStatusesSelect';
import { PaymentStatusSelect } from '@/features/invoice_reports/components/Search/components/PaymentStatusSelect';
import { useElementWidth } from '@/hooks/useElementWidth';
import { BaseOption } from '@/hooks/useTypeToOptions';
import {
  ButtonV2,
  Form,
  FormSelectProps,
  Label,
} from '@moneyforward/ap-frontend-components';
import { OfficeInvoiceReportSettings } from 'ap-openapi';
import { Col, Row } from 'antd';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useConfigForm } from '../../hooks/useConfigForm';
import { InvoiceReportsSearchFormData } from '../../hooks/useSearchForm';
import styles from '../../Search.module.scss';
import { AgentApplicantTargetOfficeMember } from '../AgentApplicantTargetOfficeMember';
import { DateRangeSelect } from '../DateRangeSelect';
import { FormContainer } from '../FormContainer';
import { FromInputSearch } from '../FromInputSearch';
import { InvoiceStatusesSelect } from '../InvoiceStatusesSelect';

const cx = classnames.bind(styles);

const ROW_GUTTER: [number, number] = [12, 12];

type ApPayeeSelectProps<TFieldValues extends FieldValues> = Omit<
  FormSelectProps<TFieldValues>['selectProps'],
  'options'
>;

interface SearchFormProps
  extends Pick<
    ReturnType<typeof useConfigForm>,
    'customProps' | 'checkLengthValidationRules'
  > {
  form: UseFormReturn<InvoiceReportsSearchFormData>;
  onSubmit: (data: InvoiceReportsSearchFormData) => void;
  onReset: () => void;
  agentApplicantDefaultOptions?: BaseOption[];
  appPayeeDefaultOptions: BaseOption[];
  canAgentEntry: boolean;
  viewerOptions?: BaseOption[];
  showViewerField?: boolean;
  isLoading?: boolean;
  onActionButtonsWidthChange?: (width: number) => void;
  officeInvoiceReportSettings?: OfficeInvoiceReportSettings;
}

export const SearchForm: FC<SearchFormProps> = memo(
  ({
    form,
    onSubmit,
    onReset,
    agentApplicantDefaultOptions,
    customProps,
    checkLengthValidationRules,
    appPayeeDefaultOptions,
    canAgentEntry,
    viewerOptions,
    showViewerField = false,
    isLoading = false,
    onActionButtonsWidthChange,
    officeInvoiceReportSettings,
  }) => {
    const { control } = form;
    const { labels } = customProps;
    const actionButtonsRef = useElementWidth(onActionButtonsWidthChange);
    const containerRef = useGetPopupContainer();
    const instantPayeeSetting =
      officeInvoiceReportSettings?.instant_payee_setting;

    const apPayeeSelectProps: ApPayeeSelectProps<FieldValues> = useMemo(
      () => ({
        mode: 'multiple' as const,
        maxTagCount: 1,
        className: 'apPayee',
        popupClassName: cx('invoice-reports-ap-payee-dropdown'),
        inlineOptions: true,
        getPopupContainer: containerRef,
        ...customProps?.selectProps?.configApPayee,
        ...{ dropdownStyle: { maxHeight: 400, overflowY: 'auto' } },
      }),
      [containerRef, customProps?.selectProps?.configApPayee]
    );

    const apPayeeCaptionField = useMemo(
      (): ['payment_method_text', 'account_display_name'] => [
        'payment_method_text',
        'account_display_name',
      ],
      []
    );

    // payment_status and mf_file compress when the agent field is present to keep row 1 at 24 cols
    const proxyFieldSpan = {
      span: canAgentEntry ? 4 : 6,
      xxl: canAgentEntry ? 2 : 4,
    } as const;

    return (
      <Form<InvoiceReportsSearchFormData> formMethod={form} onSubmit={onSubmit}>
        <Row gutter={ROW_GUTTER} className={cx('search-grid')} wrap>
          {/* Row 1 */}
          <Col span={6} xxl={4}>
            <FormContainer>
              <div>
                <Label
                  text={labels.query.text}
                  tooltip={labels.query.tooltip}
                />
                <FromInputSearch
                  control={control}
                  inputProps={customProps?.inputProps?.query}
                  name='query'
                  inputId='query'
                  rules={checkLengthValidationRules}
                  testId='query-input'
                />
              </div>
            </FormContainer>
          </Col>

          <Col span={2}>
            <FormContainer>
              <KeywordCont
                control={control}
                name='number'
                labelProps={labels.number}
                inputProps={customProps?.inputProps?.number}
                rules={checkLengthValidationRules}
              />
            </FormContainer>
          </Col>

          <Col span={4} xxl={2}>
            <FormContainer>
              <div className={cx('select-wrapper')}>
                <label className={cx('label-text')}>{labels.status.text}</label>
                <InvoiceStatusesSelect control={control} name='status' />
              </div>
            </FormContainer>
          </Col>

          {canAgentEntry && (
            <Col span={4}>
              <FormContainer>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.agent_applicant.text}
                  </label>
                  <AgentApplicantTargetOfficeMember
                    control={control}
                    name='agent_applicant_office_member_ids'
                    inputId='agent_applicant_office_member_ids'
                    defaultOptions={agentApplicantDefaultOptions}
                    selectProps={customProps?.selectProps?.configAgentApplicant}
                    per={200}
                    display_all_roles
                    include_agent_applicant
                  />
                </div>
              </FormContainer>
            </Col>
          )}

          <Col {...proxyFieldSpan}>
            <FormContainer>
              <div className={cx('select-wrapper')}>
                <label className={cx('label-text')}>
                  {labels.payment_status.text}
                </label>
                <PaymentStatusSelect control={control} name='payment_status' />
              </div>
            </FormContainer>
          </Col>

          <Col {...proxyFieldSpan}>
            <FormContainer>
              <div className={cx('select-wrapper')}>
                <label className={cx('label-text')}>
                  {labels.mf_file.text}
                </label>
                <InvoiceMFFileStatusesSelect
                  control={control}
                  name='mf_file'
                  formContext={form}
                />
              </div>
            </FormContainer>
          </Col>

          {/* Row 2 */}
          <Col span={6} xxl={4}>
            <FormContainer>
              <div className={cx('select-wrapper-app-payee')}>
                <label className={cx('label-text')}>
                  {labels.ap_payee.text}
                </label>
                <ApPayee
                  control={control}
                  name='ap_payee_ids'
                  inputId='payee'
                  formContext={undefined}
                  defaultOptions={appPayeeDefaultOptions}
                  selectProps={apPayeeSelectProps}
                  instantPayeeActivatedValue={
                    instantPayeeSetting?.enabled
                      ? instantPayeeSetting?.instant_payee_search_option?.id
                      : undefined
                  }
                  include_inactive
                  useDisplayName
                  hideInstantPayeeOnSearch
                  captionField={apPayeeCaptionField}
                  multipleCaptionLine
                  showCode
                />
              </div>
            </FormContainer>
          </Col>

          <Col span={6} xxl={4}>
            <FormContainer>
              <div className={cx('total-amount-wrapper')}>
                <label className={cx('label-text')}>
                  {labels.total_amount.text}
                </label>
                <div className={cx('input-wrapper-number')}>
                  <Amount control={control} name='total_amount_from' />
                  <span className={cx('amount-range-separator')}>~</span>
                  <Amount control={control} name='total_amount_to' />
                </div>
              </div>
            </FormContainer>
          </Col>

          {showViewerField && (
            <Col span={6} xxl={4}>
              <FormContainer>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.viewer.text}
                  </label>
                  <OfficeMember<InvoiceReportsSearchFormData>
                    control={control}
                    name='viewer_ids'
                    inputId='viewer_ids'
                    testId='viewer-ids'
                    defaultOptions={viewerOptions}
                    selectProps={customProps?.selectProps?.configViewer}
                  />
                </div>
              </FormContainer>
            </Col>
          )}

          <Col span={6} xxl={4}>
            <FormContainer>
              <DateRangeSelect
                control={control}
                name='created_date'
                labelProps={labels.created_date}
              />
            </FormContainer>
          </Col>

          <Col span={6} xxl={4}>
            <FormContainer>
              <DateRangeSelect
                control={control}
                name='submitted_date'
                labelProps={labels.submitted_date}
              />
            </FormContainer>
          </Col>

          {/* Row 3 */}
          <Col span={6} xxl={4}>
            <FormContainer>
              <DateRangeSelect
                control={control}
                name='approved_date'
                labelProps={labels.approved_date}
              />
            </FormContainer>
          </Col>

          <Col span={6} xxl={4}>
            <FormContainer>
              <DateRangeSelect
                control={control}
                name='book_date'
                labelProps={labels.book_date}
              />
            </FormContainer>
          </Col>

          <Col span={6} xxl={4}>
            <FormContainer>
              <DateRangeSelect
                control={control}
                name='due_date'
                labelProps={labels.due_date}
              />
            </FormContainer>
          </Col>

          <Col span={6} xxl={4}>
            <FormContainer>
              <DateRangeSelect
                control={control}
                name='payment_date'
                labelProps={labels.payment_date}
              />
            </FormContainer>
          </Col>
        </Row>

        {/* Action Buttons */}
        <div ref={actionButtonsRef} className={cx('action-buttons')}>
          <ButtonV2
            size='sm'
            isTertiary
            onClick={onReset}
            className={cx('reset-button')}
          >
            {labels.reset_button}
          </ButtonV2>
          <ButtonV2
            size='sm'
            isSecondary
            type='submit'
            buttonContainerClassName={cx('search-button-container')}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {labels.search_button}
          </ButtonV2>
        </div>
      </Form>
    );
  }
);

SearchForm.displayName = 'SearchForm';
